/* eslint-disable react-refresh/only-export-components */
import { lazy } from "react";
import {
  Navigate,
  useLocation,
  useParams,
  type RouteObject,
} from "react-router-dom";
import SuspenseWrapper from "../SuspenseWrapper";
import {
  dashboardPath,
  isLegacySlugHash,
  isRootStorefront,
} from "../../config/storefrontRouting";
import { productsRoutes } from "./products.route";
import { usersRolesRoutes } from "./users-roles.route";
import { commerceRoutes } from "./commerce.routes";
import { settingsRoute } from "./settings.routes";
import RootLayout from "../../layouts/RootLayout";
import { AuthGuard } from "../../components/guards";

const DashboardLandingPage = lazy(
  () => import("../../pages/DashboardLandingPage"),
);
const DashboardOverviewPage = lazy(
  () => import("../../pages/DashboardOverviewPage"),
);
const PublicWebsitePage = lazy(
  () => import("../../pages/website/PublicWebsitePage"),
);
const NotFound = lazy(() => import("../../pages/NotFound"));
const PublicProductPage = lazy(
  () => import("../../pages/website/PublicProductPage"),
);
const PublicAboutPage = lazy(
  () => import("../../pages/website/PublicAboutPage"),
);
const PublicContactPage = lazy(
  () => import("../../pages/website/PublicContactPage"),
);
const PublicProductDetailsPage = lazy(
  () => import("../../pages/website/PublicProductDetailsPage"),
);
const PublicProfilePage = lazy(
  () => import("../../pages/website/PublicProfilePage"),
);
const PublicCartPage = lazy(
  () => import("../../pages/website/PublicCartPage"),
);
const PublicCheckoutPage = lazy(
  () => import("../../pages/website/PublicCheckoutPage"),
);
const PublicOrderDetailsPage = lazy(
  () => import("../../pages/website/PublicOrderDetailsPage"),
);
const HomepageEditorPage = lazy(
  () => import("../../pages/website/HomepageEditorRedirectPage"),
);
const ContactInquiriesPage = lazy(
  () => import("../../pages/ContactInquiriesPage"),
);
const WebsiteUsersPage = lazy(() => import("../../pages/WebsiteUsersPage"));
const WebsiteUserDetailsPage = lazy(
  () => import("../../pages/WebsiteUserDetailsPage"),
);

function RootStorefrontEntry() {
  const location = useLocation();
  return isLegacySlugHash(location.hash) ? (
    <NotFound />
  ) : (
    <PublicWebsitePage />
  );
}

function LegacyDashboardRedirect({ prefix }: { prefix: string }) {
  const location = useLocation();
  const { "*": remainder = "" } = useParams();
  const suffix = remainder ? `/${remainder}` : "";
  return (
    <Navigate
      replace
      to={`${dashboardPath(`${prefix}${suffix}`)}${location.search}${location.hash}`}
    />
  );
}

function PublicCategoriesPage() {
  return (
    <SuspenseWrapper>
      <PublicProductPage />
    </SuspenseWrapper>
  );
}

const slugStorefrontRoutes: RouteObject[] = [
  {
    path: "/site/:slug",
    element: (
      <SuspenseWrapper>
        <PublicWebsitePage />
      </SuspenseWrapper>
    ),
  },
  {
    path: "/site/:slug/categories/:categoryId?",
    element: <PublicCategoriesPage />,
  },
  {
    path: "/site/:slug/products",
    element: (
      <SuspenseWrapper>
        <PublicProductPage />
      </SuspenseWrapper>
    ),
  },
  {
    path: "/site/:slug/about",
    element: (
      <SuspenseWrapper>
        <PublicAboutPage />
      </SuspenseWrapper>
    ),
  },
  {
    path: "/site/:slug/contact",
    element: (
      <SuspenseWrapper>
        <PublicContactPage />
      </SuspenseWrapper>
    ),
  },
  {
    path: "/site/:slug/products/:id",
    element: (
      <SuspenseWrapper>
        <PublicProductDetailsPage />
      </SuspenseWrapper>
    ),
  },
  {
    path: "/site/:slug/profile",
    element: (
      <SuspenseWrapper>
        <PublicProfilePage />
      </SuspenseWrapper>
    ),
  },
  {
    path: "/site/:slug/cart",
    element: (
      <SuspenseWrapper>
        <PublicCartPage />
      </SuspenseWrapper>
    ),
  },
  {
    path: "/site/:slug/checkout",
    element: (
      <SuspenseWrapper>
        <PublicCheckoutPage />
      </SuspenseWrapper>
    ),
  },
  {
    path: "/site/:slug/orders/:id",
    element: (
      <SuspenseWrapper>
        <PublicOrderDetailsPage />
      </SuspenseWrapper>
    ),
  },
];

const tenantRootStorefrontRoutes: RouteObject[] = [
  {
    path: "/categories/:categoryId?",
    element: <PublicCategoriesPage />,
  },
  {
    path: "/products",
    element: (
      <SuspenseWrapper>
        <PublicProductPage />
      </SuspenseWrapper>
    ),
  },
  {
    path: "/about",
    element: (
      <SuspenseWrapper>
        <PublicAboutPage />
      </SuspenseWrapper>
    ),
  },
  {
    path: "/contact",
    element: (
      <SuspenseWrapper>
        <PublicContactPage />
      </SuspenseWrapper>
    ),
  },
  {
    path: "/products/:id",
    element: (
      <SuspenseWrapper>
        <PublicProductDetailsPage />
      </SuspenseWrapper>
    ),
  },
  {
    path: "/profile",
    element: (
      <SuspenseWrapper>
        <PublicProfilePage />
      </SuspenseWrapper>
    ),
  },
  {
    path: "/cart",
    element: (
      <SuspenseWrapper>
        <PublicCartPage />
      </SuspenseWrapper>
    ),
  },
  {
    path: "/checkout",
    element: (
      <SuspenseWrapper>
        <PublicCheckoutPage />
      </SuspenseWrapper>
    ),
  },
  {
    path: "/profile/orders/:id",
    element: (
      <SuspenseWrapper>
        <PublicOrderDetailsPage />
      </SuspenseWrapper>
    ),
  },
];

function dashboardRoute(): RouteObject {
  return {
    path: isRootStorefront() ? "/dashboard" : "/",
    element: (
      <AuthGuard>
        <RootLayout />
      </AuthGuard>
    ),
    children: [
      {
        index: true,
        element: (
          <SuspenseWrapper>
            <DashboardLandingPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: "dashboard",
        element: (
          <SuspenseWrapper>
            <DashboardLandingPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: "overview",
        element: (
          <SuspenseWrapper>
            <DashboardOverviewPage />
          </SuspenseWrapper>
        ),
      },
      ...productsRoutes,
      ...usersRolesRoutes,
      ...commerceRoutes,
      {
        path: "contact-inquiries",
        element: (
          <SuspenseWrapper>
            <ContactInquiriesPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: "website-users",
        element: (
          <SuspenseWrapper>
            <WebsiteUsersPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: "website-users/:id",
        element: (
          <SuspenseWrapper>
            <WebsiteUserDetailsPage />
          </SuspenseWrapper>
        ),
      },
      settingsRoute,
      {
        path: "website/homepage",
        element: (
          <SuspenseWrapper>
            <HomepageEditorPage />
          </SuspenseWrapper>
        ),
      },
    ],
  };
}

function legacyDashboardAliases(): RouteObject[] {
  if (!isRootStorefront()) return [];
  return [
    { path: "/overview", element: <LegacyDashboardRedirect prefix="/overview" /> },
    { path: "/orders/*", element: <LegacyDashboardRedirect prefix="/orders" /> },
    {
      path: "/products/list/*",
      element: <LegacyDashboardRedirect prefix="/products/list" />,
    },
    {
      path: "/products/categories/*",
      element: <LegacyDashboardRedirect prefix="/products/categories" />,
    },
    {
      path: "/products/price-rules/*",
      element: <LegacyDashboardRedirect prefix="/products/price-rules" />,
    },
    {
      path: "/products/tags/*",
      element: <LegacyDashboardRedirect prefix="/products/tags" />,
    },
    {
      path: "/products/variant-attributes/*",
      element: <LegacyDashboardRedirect prefix="/products/variant-attributes" />,
    },
    {
      path: "/commerce/*",
      element: <LegacyDashboardRedirect prefix="/commerce" />,
    },
    {
      path: "/users-rules/*",
      element: <LegacyDashboardRedirect prefix="/users-rules" />,
    },
    {
      path: "/contact-inquiries",
      element: <LegacyDashboardRedirect prefix="/contact-inquiries" />,
    },
    { path: "/settings", element: <LegacyDashboardRedirect prefix="/settings" /> },
    {
      path: "/website/*",
      element: <LegacyDashboardRedirect prefix="/website" />,
    },
  ];
}

export function createPublicRoutes(): RouteObject[] {
  const storefrontRoutes = isRootStorefront()
    ? [
        {
          path: "/",
          element: (
            <SuspenseWrapper>
              <RootStorefrontEntry />
            </SuspenseWrapper>
          ),
        },
        ...tenantRootStorefrontRoutes,
      ]
    : slugStorefrontRoutes;
  return [
    ...storefrontRoutes,
    dashboardRoute(),
    ...legacyDashboardAliases(),
  ];
}
